import { Button, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useSkatConnection } from '@features/vatDeclarations/contexts/skatConnectionContext'

import { ModalId } from '../../../../../../../../enums/modalId'
import { useVatDeclaration } from '../../../../../../context/vatDeclarationContext'
import { VatDeclarationStatus } from '../../../../../../enums/vatDeclarationStatus'
import { useTrackVatDeclarationEvent } from '../../../../../../hooks/useTrackVatDeclarationEvent'
import { SubmitButtonVariant } from '../../../../../../utils/getSubmitButtonVariant'

interface SideActionsPrimaryButtonDKProps {
  buttonVariant?: SubmitButtonVariant | null
  onShowIntegrationForm?: (show: boolean) => void
}

export const SideActionsPrimaryButtonDK = ({
  buttonVariant,
  onShowIntegrationForm,
}: SideActionsPrimaryButtonDKProps): ReactElement | null => {
  const { t } = useTranslation()
  const { isSkatConnected, isAlreadyApproved } = useSkatConnection()
  const { status } = useVatDeclaration()
  const trackEvent = useTrackVatDeclarationEvent()
  const { open: openRegisterPaymentModal } = useModal(ModalId.VatDeclarationsRegisterPeriodPaymentModal)
  const { open: openSettlementModal } = useModal(ModalId.VatDeclarationsSettlePeriodModal)

  const shouldRenderPaymentButton =
    status === VatDeclarationStatus.ReadyToPay || status === VatDeclarationStatus.OverduePayment

  const handleSubmitVat = useCallback(() => {
    onShowIntegrationForm?.(true)
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      buttonClick: 'Submit VAT',
      flow: 'Success',
      step: '1 - Transfer to',
    })
  }, [onShowIntegrationForm, trackEvent])

  const handleSubmitManually = useCallback(() => {
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      buttonClick: 'Submit manually',
    })

    openSettlementModal()
  }, [openSettlementModal, trackEvent])

  const handleRegisterPaymentButtonClick = useCallback(() => {
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      buttonClick: 'Register payment',
    })
    openRegisterPaymentModal()
  }, [openRegisterPaymentModal, trackEvent])

  if (shouldRenderPaymentButton) {
    return <Button onClick={handleRegisterPaymentButtonClick}>{t('vat_declarations.actions.register_payment')}</Button>
  }

  if (isAlreadyApproved) {
    return null
  }

  if (buttonVariant === SubmitButtonVariant.Manual) {
    return (
      <Button variant="outlined" onClick={handleSubmitManually}>
        {t('vat_declarations.submit_manually')}
      </Button>
    )
  }

  if (buttonVariant === SubmitButtonVariant.Integration && isSkatConnected) {
    return <Button onClick={handleSubmitVat}>{t('vat_declarations.submit_vat')}</Button>
  }

  return null
}
